import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Divider, List, Stack, Typography } from 'components/ui';
import { useError } from 'contexts/error';
import { NotificationsLink } from 'utils/link';
import NotificationsQuery from 'queries/notification/index';
import UnreadNotificationsCountQuery from 'queries/notification/unread_count';
import MarkAllAsReadMutation from 'mutations/notification/mark_all_as_read';
import NotificationItem from 'components/notification/notification_item';
import LoaderContainer from 'components/layout/loader_container';
import InfoContainer from 'components/info_container';
import theme from 'theme';

const Dropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const setError = useError();

  const { data = { notifications: { notifications: [], unreadTotal: 0 } }, loading } = useQuery(
    NotificationsQuery,
    {
      variables: { page: 1 },
      cachePolicy: 'cache-and-network',
      onError: () => setError('error.notification.index'),
    }
  );

  const [markAllAsRead, { loading: marking }] = useMutation(MarkAllAsReadMutation, {
    refetchQueries: [
      { query: UnreadNotificationsCountQuery },
      { query: NotificationsQuery, variables: { page: 1 } },
    ],
    onError: () => setError('error.notification.mark_all_as_read'),
  });

  const handleShowAll = useCallback(() => {
    onClose?.();
    navigate(NotificationsLink());
  }, [navigate, onClose]);

  const notifications = data.notifications.notifications;
  const hasUnread = data.notifications.unreadTotal > 0;
  const isEmpty = !loading && notifications.length === 0;

  return (
    <Box sx={{ width: theme.width.notificationDropdown, maxWidth: '100vw' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          <FormattedMessage id="notification.title" />
        </Typography>
        <Button
          size="small"
          variant="text"
          disabled={!hasUnread || marking}
          onClick={() => markAllAsRead({ variables: {} })}
        >
          <FormattedMessage id="notification.mark_all_as_read" />
        </Button>
      </Stack>
      <Divider />
      <Box sx={{ maxHeight: theme.height.notificationDropdown, overflowY: 'auto' }}>
        <LoaderContainer loading={loading} wrap={false} keepRenderedContent={true}>
          {isEmpty && (
            <Box sx={{ p: 2 }}>
              <InfoContainer>
                <FormattedMessage id="notification.empty" />
              </InfoContainer>
            </Box>
          )}
          <List disablePadding>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={onClose}
                compact
              />
            ))}
          </List>
        </LoaderContainer>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Button size="small" variant="text" onClick={handleShowAll}>
          <FormattedMessage id="notification.show_all" />
        </Button>
      </Box>
    </Box>
  );
};

export default Dropdown;
